/**
 * Pomodoro page — the full-screen focus timer.
 *
 * Rendered by the root layout inside a fixed overlay layer, so it just fills
 * its container and scrolls on its own. Timer state lives in
 * `usePomodoroStore` (persisted); the finish watcher is mounted at the root,
 * so this page is purely a view + controls.
 *
 * Close (✕ / Esc): history back when the app has in-app history, otherwise
 * navigate to /tasks. Esc is ignored while a Radix dialog or the task preview
 * drawer is open — those consume Esc first.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { Icon } from '@k-os/ui';
import { useTask } from '../../api/tasks';
import { usePreviewStore } from '../../lib/preview-store';
import { MODE_LABELS, type PomodoroMode } from '../../lib/pomodoro-timer';
import { usePomodoroClock, usePomodoroStore } from '../../lib/pomodoro-store';
import { requestNotificationPermissionOnce } from '../../lib/use-pomodoro-finish';
import { PomodoroTaskList } from './PomodoroTaskList';
import styles from './PomodoroPage.module.css';

const MODES: PomodoroMode[] = ['pomodoro', 'short', 'long'];

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    tag === 'BUTTON' ||
    el.isContentEditable ||
    !!el.closest('[role="dialog"]')
  );
}

export function PomodoroPage() {
  const router = useRouter();
  const navigate = useNavigate();
  const mode = usePomodoroStore((s) => s.mode);
  const status = usePomodoroStore((s) => s.status);
  const focusTaskId = usePomodoroStore((s) => s.focusTaskId);
  const start = usePomodoroStore((s) => s.start);
  const pause = usePomodoroStore((s) => s.pause);
  const reset = usePomodoroStore((s) => s.reset);
  const switchMode = usePomodoroStore((s) => s.switchMode);
  const { label } = usePomodoroClock();
  const focusTask = useTask(focusTaskId ?? undefined);
  const [pendingMode, setPendingMode] = useState<PomodoroMode | null>(null);

  const running = status === 'running';

  const close = useCallback(() => {
    if (router.history.canGoBack()) router.history.back();
    else void navigate({ to: '/tasks' });
  }, [router, navigate]);

  const toggle = useCallback(() => {
    if (running) {
      pause();
    } else {
      requestNotificationPermissionOnce();
      start();
    }
  }, [running, pause, start]);

  const requestMode = (next: PomodoroMode) => {
    if (next === mode) return;
    if (status === 'idle') switchMode(next);
    else setPendingMode(next);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (e.defaultPrevented) return;
        if (usePreviewStore.getState().taskId) return; // drawer consumes Esc
        if (document.querySelector('[role="dialog"]')) return; // Radix dialogs
        close();
      } else if (e.key === ' ' || e.code === 'Space') {
        if (e.metaKey || e.ctrlKey || e.altKey) return;
        if (isTypingTarget(e.target)) return;
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close, toggle]);

  const focusTitle = focusTask.data?.task.title;
  const drawerOpen = usePreviewStore((s) => s.taskId !== null);
  // Breaks are for resting: no task list, no "working on" line.
  const isBreak = mode !== 'pomodoro';

  return (
    <div className={`${styles.page} ${styles[mode]}`}>
      <button type="button" className={`${styles.close} ${drawerOpen ? styles.closeShifted : ''}`}
        onClick={close} aria-label="Close timer">
        <Icon name="x" size={18} />
      </button>

      <div className={styles.inner}>
        <div className={styles.card}>
          <div className={styles.tabs} role="tablist" aria-label="Timer mode">
            {MODES.map((m) => (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={m === mode}
                className={`${styles.tab} ${m === mode ? styles.tabActive : ''}`}
                onClick={() => requestMode(m)}
              >
                {MODE_LABELS[m]}
              </button>
            ))}
          </div>

          <div className={styles.clock} role="timer" aria-live="off">
            {label}
          </div>

          <div className={styles.controls}>
            <button type="button" className={styles.primary} onClick={toggle}>
              {running ? 'Pause' : status === 'paused' ? 'Resume' : 'Start'}
            </button>
            {status !== 'idle' && (
              <button type="button" className={styles.secondary} onClick={reset}>
                Reset
              </button>
            )}
          </div>

          {pendingMode && (
            <div className={styles.confirm} role="group" aria-label="Confirm mode switch">
              <span>Switch to {MODE_LABELS[pendingMode]}? The current timer will be reset.</span>
              <button
                type="button"
                className={styles.confirmYes}
                onClick={() => {
                  switchMode(pendingMode);
                  setPendingMode(null);
                }}
              >
                Switch
              </button>
              <button
                type="button"
                className={styles.confirmNo}
                onClick={() => setPendingMode(null)}
              >
                Keep going
              </button>
            </div>
          )}

          {isBreak ? (
            <p className={styles.breakNote}>Step away from the screen — tasks can wait.</p>
          ) : (
            <p className={styles.working}>
              {focusTaskId && focusTitle ? (
                <>
                  Working on: <strong>{focusTitle}</strong>
                </>
              ) : (
                <span className={styles.muted}>Pick a task to focus on</span>
              )}
            </p>
          )}
        </div>

        {!isBreak && <PomodoroTaskList />}
      </div>
    </div>
  );
}
